import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import SignIn from './pages/SignIn/SignIn';
import './App.scss';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAppDispatch, useAppSelector } from './app/hooks';
import { selectErrorMessage, setError, selectCurrentTheme } from './reducers/globalSlice';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { courmacsTheme } from './styles/theme';
import './styles/scrollReveal.scss';
import RequireAuth from './RequireAuth';
import CookieConsent from './components/CookieConsent/CookieConsent';
import AnalyticsRouteTracker from './components/AnalyticsRouteTracker';
import ScrollToTop from './components/ScrollToTop';
import PageLoader from './components/PageLoader/PageLoader';

const LocalTradesHomepage = lazy(() => import('./pages/LocalTradesHomepage/LocalTradesHomepage'));
const PlumberLanding = lazy(() => import('./pages/PlumberLanding/PlumberLanding'));
const AgencyLanding = lazy(() => import('./pages/AgencyLanding/AgencyLanding'));
const AgencyRegister = lazy(() => import('./pages/AgencyRegister/AgencyRegister'));
const AgencyBusinesses = lazy(() => import('./pages/AgencyBusinesses/AgencyBusinesses'));
const SeoNotFound = lazy(() => import('./pages/SeoLanding/SeoNotFound'));
const ResetPassword = lazy(() => import('./pages/ResetPassword/ResetPassword'));
const BillingStripeReturn = lazy(() => import('./pages/BillingStripeReturn/BillingStripeReturn'));
const LegalPage = lazy(() => import('./pages/Legal/LegalPage'));
const AdminInvokeFunction = lazy(() => import('./pages/AdminInvokeFunction/AdminInvokeFunction'));
const AdminSkill = lazy(() => import('./pages/AdminSkill/AdminSkill'));
const AdminSkillAction = lazy(() => import('./pages/AdminSkillAction/AdminSkillAction'));
const AdminSkillActionOutput = lazy(() => import('./pages/AdminSkillActionOutput/AdminSkillActionOutput'));
const AdminSkillDomain = lazy(() => import('./pages/AdminSkillDomain/AdminSkillDomain'));
const Service = lazy(() => import('./pages/Service/Service'));
const BusinessCatalog = lazy(() => import('./components/details/BusinessCatalog/BusinessCatalog'));

const lazyWithAudioProvider = <P extends object>(
  loader: () => Promise<{ default: React.ComponentType<P> }>
) =>
  lazy(async () => {
    const [routeModule, audioModule] = await Promise.all([
      loader(),
      import('./context/AssistantContext'),
    ]);
    const Component = routeModule.default;
    const AudioProvider = audioModule.AudioProvider;

    return {
      default: (props: P) => (
        <AudioProvider>
          <Component {...props} />
        </AudioProvider>
      ),
    };
  });

const BusinessSetup = lazyWithAudioProvider(() => import('./pages/BusinessSetup/BusinessSetup'));
const Homepage = lazyWithAudioProvider(() => import('./pages/Homepage/Homepage'));
const QuickSetup = lazyWithAudioProvider(() => import('./pages/QuickSetup/QuickSetup'));
const Portal = lazyWithAudioProvider(() => import('./pages/Portal/Portal'));

declare global {
  interface Window {
    __brandStylesheetId?: string;
    __brandStyleNodes?: (HTMLLinkElement | HTMLStyleElement)[];
  }
}

const removeBrandStyles = (forget = false) => {
  const nodes = window.__brandStyleNodes ?? [];
  nodes.forEach(node => node.parentNode?.removeChild(node));
  if (forget) {
    window.__brandStyleNodes = [];
    window.__brandStylesheetId = undefined;
  }
};

function App() {
  const dispatch = useAppDispatch();
  const errorMessage = useAppSelector(selectErrorMessage);
  const currentTheme = useAppSelector(selectCurrentTheme);
  const business = useAppSelector(state => state.business.business);

  useEffect(() => {
    const theme: any = courmacsTheme;
    Object.keys(theme).forEach(key => {
      const value = theme[key];
      document.documentElement.style.setProperty(key, value);
    });

  }, [currentTheme]);

  useEffect(() => {
    const rawUrl = business?.stylesheetUrl?.trim();
    if (!rawUrl) {
      removeBrandStyles(true);
      return;
    }

    const isAbsoluteUrl = /^https?:\/\//i.test(rawUrl) || rawUrl.startsWith("//");
    const normalizedPath = rawUrl.replace(/^\/+/, "");
    const publicRoot = (process.env.PUBLIC_URL || "").replace(/\/+$/, "");
    const href = isAbsoluteUrl ? rawUrl : `${publicRoot ? `${publicRoot}/` : "/"}${normalizedPath}`;
    const storedId = window.__brandStylesheetId;
    const storedNodes = window.__brandStyleNodes ?? [];

    if (storedId === href && storedNodes.length > 0) {
      storedNodes.forEach(node => {
        if (!document.head.contains(node)) {
          document.head.appendChild(node);
        }
      });
      return;
    }

    removeBrandStyles(true);

    const link = document.createElement("link");
    link.id = "business-stylesheet";
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
    window.__brandStylesheetId = href;
    window.__brandStyleNodes = [link];

    return () => {
      removeBrandStyles(true);
    };
  }, [business?.stylesheetUrl]);

  useEffect(() => {
    if (errorMessage) {
      toast.error(errorMessage, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "dark",
        });

        const reset = true;
        dispatch(setError({ reset }));
    }
  }, [dispatch, errorMessage]);

  return (
    <Router>
      <CookieConsent />
      <AnalyticsRouteTracker />
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Homepage />} />
          <Route path="/trades" element={<LocalTradesHomepage />} />
          <Route path="/plumbers" element={<PlumberLanding />} />
          <Route path="/ai-receptionist-resellers" element={<AgencyLanding />} />
          <Route path="/agency/register" element={<AgencyRegister />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/terms" element={<LegalPage />} />
          <Route path="/reseller-terms" element={<LegalPage />} />
          <Route path="/privacy" element={<LegalPage />} />
          <Route path="/cookies" element={<LegalPage />} />
          <Route path="/data-processing-agreement" element={<LegalPage />} />
          <Route path="/acceptable-use" element={<LegalPage />} />
          <Route path="/subprocessors" element={<LegalPage />} />
          <Route path="/billing/stripe-return" element={<BillingStripeReturn />} />
          <Route path="/sign-in" element={<SignIn />} />
          <Route
            path="/get-started"
            element={<BusinessSetup />}
          />
          <Route
            path="/quick-setup"
            element={<QuickSetup />}
          />
          <Route
            path="/portal"
            element={
              <RequireAuth>
                <Portal />
              </RequireAuth>
            }
          />
          <Route
            path="/agency/businesses"
            element={
              <RequireAuth>
                <AgencyBusinesses />
              </RequireAuth>
            }
          />
          <Route
            path="/manage-services"
            element={
              <RequireAuth>
                <Service />
              </RequireAuth>
            }
          />
          <Route
            path="/business/catalog"
            element={
              <RequireAuth>
                <BusinessCatalog initialTab="offerings" availableTabs={['offerings']} hideHowToGuide />
              </RequireAuth>
            }
          />
          <Route path="/admininvokefunction" element={<AdminInvokeFunction />} />
          <Route path="/adminskill" element={<AdminSkill />} />
          <Route path="/adminskillaction" element={<AdminSkillAction />} />
          <Route path="/adminskillactionoutput" element={<AdminSkillActionOutput />} />
          <Route path="/adminskilldomain" element={<AdminSkillDomain />} />
          <Route path="*" element={<SeoNotFound />} />
        </Routes>
      </Suspense>

      <ToastContainer />
    </Router>
  );
}

export default App;
